import { useEffect, useSyncExternalStore, type KeyboardEvent, type MouseEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { ROUTES } from '@/app/app/routing/routes'
import { useLogout } from '@/app/entities/auth/model'
import { useChangeEmailModel } from '@/app/entities/me/model/changeEmailModel'
import { useMe } from '@/common/entities/me/model/me'
import { hasUserEmail } from '@/common/entities/me/lib/profileCompleteness'
import { shouldShowEnvironmentWatermark } from '@/common/lib/environmentWatermark'
import { Button } from '@/common/ui/Form/Button'
import { Checkbox } from '@/common/ui/Form/Checkbox'
import { Fieldset } from '@/common/ui/Form/Fieldset'
import { Input } from '@/common/ui/Form'
import { EMAIL_VALIDATION } from '@/common/ui/Form/Input'
import { Modal } from '@/common/ui/Modal'
import { getRequireEmailModalDismissedQueryKey, REQUIRE_EMAIL_MODAL_ID } from './requireEmailModal.constants'

export const RequireEmailModal = () => {
    const location = useLocation()
    const queryClient = useQueryClient()
    const { me, isLoading: meLoading, isSuccess } = useMe()
    const { handleSubmit, onSubmit, register, errors, isPending } = useChangeEmailModel()
    const logoutMutation = useLogout()
    const userId = me?.id

    const canDismiss = shouldShowEnvironmentWatermark()
    const isAuthRoute = location.pathname.startsWith(ROUTES.AUTH.BASE)
    const needsEmail = !isAuthRoute && isSuccess && !meLoading && !hasUserEmail(me)

    const dismissedQueryKey = getRequireEmailModalDismissedQueryKey(userId ?? '')
    const isDismissed = useSyncExternalStore(
        onStoreChange => {
            if (!userId || !canDismiss) return () => undefined
            return queryClient.getQueryCache().subscribe(event => {
                const key = event.query.queryKey
                if (key[0] === 'require_email_modal' && key[1] === 'dismissed' && key[2] === userId) {
                    onStoreChange()
                }
            })
        },
        () => {
            if (!userId || !canDismiss) return false
            return queryClient.getQueryData<boolean>(dismissedQueryKey) === true
        },
        () => false
    )

    useEffect(() => {
        if (!userId || !hasUserEmail(me)) return
        queryClient.setQueryData(getRequireEmailModalDismissedQueryKey(userId), false)
    }, [userId, me, queryClient])

    const isOpen = needsEmail && !(canDismiss && isDismissed)

    const handleClose = () => {
        if (!canDismiss || !userId) return
        queryClient.setQueryData(getRequireEmailModalDismissedQueryKey(userId), true)
    }

    const handleLogoutClick = (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault()
        logoutMutation.mutate()
    }

    const handleLogoutKeyDown = (event: KeyboardEvent<HTMLAnchorElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        logoutMutation.mutate()
    }

    return (
        <Modal
            id={REQUIRE_EMAIL_MODAL_ID}
            open={isOpen}
            onClose={handleClose}
            showCloseButton={canDismiss}
            closeOnEscape={canDismiss}
            aria-label='Укажите почту'
            aria-modal='true'
        >
            <h3 className='text-lg font-bold mb-4'>Укажите почту</h3>
            <p className='text-sm text-base-content/70 mb-6'>
                Для продолжения работы необходимо указать и подтвердить адрес электронной почты. Чтобы сменить аккаунт,{' '}
                <a
                    href={ROUTES.AUTH.LOGIN}
                    className='underline text-[var(--pixorion-light-violet)]'
                    onClick={handleLogoutClick}
                    onKeyDown={handleLogoutKeyDown}
                    tabIndex={0}
                    aria-label='Выйти из профиля'
                >
                    выйдите из профиля
                </a>
                .
            </p>
            <form method='post' className='modal-form' onSubmit={handleSubmit(onSubmit)}>
                <div className='modal-form-fields flex flex-col gap-[1.875rem]'>
                    <Fieldset label='Почта' required errorText={errors.email?.message}>
                        <Input
                            className='w-full'
                            size='lg'
                            type='email'
                            autoComplete='email'
                            placeholder='Введите почту'
                            aria-label='Email'
                            {...register('email', {
                                required: EMAIL_VALIDATION.required,
                                pattern: EMAIL_VALIDATION.pattern,
                            })}
                        />
                    </Fieldset>
                    <Fieldset>
                        <Checkbox
                            id='require-email-subscribe-marketing'
                            aria-label='Согласие на рассылки'
                            {...register('subscribe_marketing')}
                        />
                        <label htmlFor='require-email-subscribe-marketing' className='ml-2'>
                            Согласен получать рекламные и информационные рассылки
                        </label>
                    </Fieldset>
                    <Fieldset errorText={errors.terms_of_service?.message} required>
                        <Checkbox
                            id='require-email-terms-of-service'
                            aria-label='Согласие с публичной офертой и политикой обработки персональных данных'
                            {...register('terms_of_service', {
                                required: 'Необходимо согласиться с условиями пользовательского соглашения',
                            })}
                        />
                        <label htmlFor='require-email-terms-of-service' className='ml-2'>
                            Согласен с условиями{' '}
                            <a href='/eula.pdf' target='_blank' className='underline whitespace-nowrap'>
                                публичной оферты
                            </a>{' '}
                            и подтверждаю свое согласие с{' '}
                            <a href='/privacy.pdf' target='_blank' className='underline'>
                                политикой в отношении обработки персональных данных
                            </a>
                            .
                        </label>
                    </Fieldset>
                    {errors.root && <p className='text-sm text-red-500'>{errors.root.message}</p>}
                </div>
                <div className='modal-form-actions'>
                    <Button
                        className='w-full'
                        emphasis='accent'
                        size='xl'
                        type='submit'
                        disabled={isPending || logoutMutation.isPending}
                        loading={isPending}
                        aria-label='Сохранить почту'
                    >
                        Сохранить
                    </Button>
                </div>
            </form>
        </Modal>
    )
}
